import { SimplePaletteColorOptions } from "@mui/material";

import loginBackground from "./assets/img/login-compressed.jpg";
import loginLogoAgrotrade from "./assets/img/variants/agrotrade/login-logo.png";
import navbarLogoAgrotrade from "./assets/img/variants/agrotrade/navbar-logo.svg";
import faviconCleverFarm from "./assets/img/variants/cleverfarm/favicon.svg";
import loginlogo from "./assets/img/variants/cleverfarm/login-logo.png";
import navbarLogo from "./assets/img/variants/cleverfarm/navbar-logo.svg";
import faviconMyMex from "./assets/img/variants/mymex/favicon.ico";
import loginLogoMyMex from "./assets/img/variants/mymex/login-logo.svg";
import navbarLogoMyMex from "./assets/img/variants/mymex/navbar-logo.svg";
import faviconSpzo from "./assets/img/variants/spzo/favicon.svg";
import loginBackgroundSpzo from "./assets/img/variants/spzo/login-background.jpg";
import loginLogoSpzo from "./assets/img/variants/spzo/login-logo.svg";
import navbarLogoSpzo from "./assets/img/variants/spzo/navbar-logo.svg";
import { COLORS } from "./shared/components/misc/CfTextBadge/CfTextBadge";
import { Variant } from "./shared/variants/variants";

type Assets = {
  navbar: {
    src: string;
    width: string;
  };
  login: {
    src: string;
    style: React.CSSProperties;
    background: string;
  };
  favicon: string | null;
};

type Precision = {
  button: Record<
    | "historyPotential"
    | "biomonitoring"
    | "soilSamples"
    | "yieldMaps"
    | "managementZones",
    {
      background: string;
      backgroundHover: string;
    }
  >;
};

type CfColors = CfPrimaryColors & typeof CF_SHARED_COLORS;

declare module "@mui/material/styles" {
  interface Palette {
    avatar: {
      primary: string;
      secondary: string;
    };
    cf: CfColors;
  }

  interface PaletteOptions {
    avatar: {
      primary: string;
      secondary: string;
    };
    cf: CfColors;
  }

  interface Theme {
    assets: Assets;
    precision: Precision;
  }
}

const PRECISION_SERVICES: Record<Variant, Precision> = {
  cleverfarm: {
    button: {
      historyPotential: {
        background: "#EBA63F",
        backgroundHover: "#E49218",
      },
      biomonitoring: {
        background: "#4CAC84",
        backgroundHover: "#3F8E6D",
      },
      soilSamples: {
        background: "#A16A00",
        backgroundHover: "#734C00",
      },
      yieldMaps: {
        background: "#8100A1",
        backgroundHover: "#5C0073",
      },
      managementZones: {
        background: "#8100A1",
        backgroundHover: "#5C0073",
      },
    },
  },
  mymex: {
    button: {
      historyPotential: {
        background: "#BB1D3D",
        backgroundHover: "#9F1934",
      },
      biomonitoring: {
        background: "#729932",
        backgroundHover: "#587823",
      },
      soilSamples: {
        background: "#A16A00",
        backgroundHover: "#734C00",
      },
      yieldMaps: {
        background: "#8100A1",
        backgroundHover: "#5C0073",
      },
      managementZones: {
        background: "#8100A1",
        backgroundHover: "#5C0073",
      },
    },
  },
  get agrotrade() {
    return { button: PRECISION_SERVICES.cleverfarm.button };
  },
  get spzo() {
    return { button: PRECISION_SERVICES.cleverfarm.button };
  },
};

// TODO once moved away from webpack, make this part of a build step
const ASSETS: Record<Variant, Assets> = {
  cleverfarm: {
    login: {
      src: loginlogo,
      style: {
        maxWidth: "74px",
        marginTop: 0,
      },
      background: loginBackground,
    },
    navbar: {
      src: navbarLogo,
      width: "130px",
    },
    favicon: faviconCleverFarm,
  },
  mymex: {
    login: {
      src: loginLogoMyMex,
      style: {
        marginTop: "16px",
      },
      background: loginBackground,
    },
    navbar: {
      src: navbarLogoMyMex,
      width: "134px",
    },
    favicon: faviconMyMex,
  },
  agrotrade: {
    login: {
      src: loginLogoAgrotrade,
      style: {
        marginTop: "32px",
      },
      background: loginBackground,
    },
    navbar: {
      src: navbarLogoAgrotrade,
      width: "163px",
    },
    favicon: null,
  },
  spzo: {
    login: {
      src: loginLogoSpzo,
      style: {
        width: "220px",
        marginTop: "32px",
      },
      background: loginBackgroundSpzo,
    },
    navbar: {
      src: navbarLogoSpzo,
      width: "150px",
    },
    favicon: faviconSpzo,
  },
};

export const COLOR_SECONDARY: Record<Variant, SimplePaletteColorOptions> = {
  cleverfarm: {
    light: "#FFEDB8",
    main: "#EBA607",
    dark: "#E68E27",
  },
  mymex: {
    light: "#BAE6DB",
    main: "#729932",
    dark: "#587823",
  },
  agrotrade: {
    light: "#99D9BF",
    main: "#00A652",
    dark: "#007A3C",
  },
  spzo: {
    light: "#FFE9B3",
    main: "#FFB600",
    dark: "#E09A00",
  },
};

export const COLOR_PRIMARY = {
  cleverfarm: {
    light: "#BAE6DB",
    main: "#00a179",
    dark: "#008267",
  },
  mymex: {
    light: "#BAE6DB",
    main: "#729932",
    dark: "#587823",
  },
  agrotrade: {
    light: "#F8B8BB",
    main: "#ED1D25",
    dark: "#B0161B",
  },
  spzo: {
    light: "#B3D9C6",
    main: "#00783F",
    dark: "#005C30",
  },
} as const satisfies Record<Variant, SimplePaletteColorOptions>;

const AVATAR: Record<Variant, Record<"primary" | "secondary", string>> = {
  cleverfarm: {
    primary: COLOR_SECONDARY.cleverfarm.main,
    secondary: COLOR_PRIMARY.cleverfarm.dark,
  },
  mymex: {
    primary: "#BB1D3D",
    secondary: "#587823",
  },
  agrotrade: {
    primary: COLOR_PRIMARY.agrotrade.main,
    secondary: COLOR_PRIMARY.agrotrade.dark,
  },
  spzo: {
    primary: COLOR_SECONDARY.spzo.main,
    secondary: COLOR_PRIMARY.spzo.dark,
  },
};

export const COLOR_TIMELINE = {
  interval: COLOR_PRIMARY.cleverfarm.main,
  isEditing: "#B3DBD1",
  handwork: COLORS.VIOLET_PLUM,
  savedHandowork: COLORS.LIGHT_PURPLE,
  overlap: COLOR_SECONDARY.cleverfarm.main,
};

export const COLOR_ERROR = {
  light: "#FFCDD2",
  main: "#DB4437",
};

export const COLOR_FONT = {
  main: "#333333",
};

// our preferred grey colors to use instead of similar ones defined in default palette
export const COLOR_GREY = {
  100: "#F2F2F2",
  200: "#E0E0E0",
  400: "#ABABAB",
  500: "#848484",
  A100: "#D5D5D5",
};

export const DEFAULT_FONT = {
  fontSize: "0.8125rem",
  fontWeight: 400,
};

type CfPrimaryColors = {
  cfPrimary: string;
  cfPrimaryDarker: string;
  cfPrimaryDarkest: string;
};

const CF_PRIMARY: Record<Variant, CfPrimaryColors> = {
  cleverfarm: {
    cfPrimary: "#00A179",
    cfPrimaryDarker: "#008967",
    cfPrimaryDarkest: "#005C45",
  },
  mymex: {
    cfPrimary: "#729932",
    cfPrimaryDarker: "#628228",
    cfPrimaryDarkest: "#4A621E",
  },
  agrotrade: {
    cfPrimary: "#ED1D25",
    cfPrimaryDarker: "#D11920",
    cfPrimaryDarkest: "#A5141A",
  },
  spzo: {
    cfPrimary: "#00783F",
    cfPrimaryDarker: "#00672F",
    cfPrimaryDarkest: "#004D23",
  },
};

const CF_SHARED_COLORS = {
  cfWhite: "#FFFFFF",
  cfLightestGrey: "#F7F7F7",
  cfLighterGrey: "#F9F9F9",
  cfLightGrey: "#EEEEEE",
  cfGrey: "#D9D9D9",
  cfDarkGrey: "#A7A7A7",
  cfDarkerGrey: "#7A7A7A",
  cfLightBlack: "#474747",
  cfBlack: "#333333",
  cfPaleGreen: "#CEE6E0",
  cfLightGreen: "#D4F4EC",
  cfLightestGreen: "#E5F8E9",
  cfGreen: "#00A179",
  cfOrange: "#EBA607",
  cfRed: "#F1543F",
  cfBlue: "#2D7FF9",
} as const;

const theme = (version: Variant) => ({
  assets: ASSETS[version],
  precision: PRECISION_SERVICES[version],
  spacing: 8,
  palette: {
    primary: COLOR_PRIMARY[version],
    secondary: COLOR_SECONDARY[version],
    error: COLOR_ERROR,
    grey: {
      ...COLOR_GREY,
    },
    avatar: AVATAR[version],
    cf: { ...CF_PRIMARY[version], ...CF_SHARED_COLORS },
    action: {
      active: CF_SHARED_COLORS.cfLightBlack,
    },
  },
  components: {
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.10)",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "standard" as const,
      },
    },
    MuiButton: {
      defaultProps: {
        color: "inherit" as const,
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: "standard" as const,
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          maxWidth: 224,
          "@media (min-width: 600px)": {
            minWidth: 150,
            flexShrink: 1,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: 13,
          backgroundColor: "#333333",
          margin: "8px 0px !important",
        },
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        day: {
          fontWeight: 400,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 42,
          paddingTop: 9,
          paddingBottom: 9,
        },
        dense: {
          minHeight: 36,
          paddingTop: 4,
          paddingBottom: 4,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        dense: {
          minHeight: 36,
          paddingTop: 4,
          paddingBottom: 4,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          marginRight: 10,
          color: COLOR_GREY[500],
          minWidth: "unset",
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        dense: {
          padding: 0,
          fontSize: "0.875rem",
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          height: "42px",
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          color: COLOR_FONT.main,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: "4px 6px",
          lineHeight: 1.25,
          ...DEFAULT_FONT,
        },
        sizeSmall: {
          padding: "9px 6px",
        },
        paddingCheckbox: {
          padding: "3px",
          paddingLeft: "3px",
        },
        head: {
          lineHeight: 1.25,
          fontWeight: 500,
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          color: COLOR_GREY[500],
          p: {
            ...DEFAULT_FONT,
            margin: 0,
          },
        },
        caption: {
          ...DEFAULT_FONT,
        },
        select: {
          ...DEFAULT_FONT,
        },
        selectIcon: {
          fontSize: "24px",
        },
        actions: {
          ...DEFAULT_FONT,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: "12px",
          margin: 0,
        },
      },
    },
  },
});

export default theme;
